import { useState } from 'react';

import type { OperationVariables, UseQuery } from '../../../types';
import { publicAppSearchQuery } from './publicAppSearchQuery';

type AppImages = {
  url_128x128: string;
  url_64x64: string;
  url_32x32: string;
  url_16x16: string;
};

type App = {
  id: string;
  canonicalId: string;
  serviceId: string;
  slug: string;
  name: string;
  images: AppImages;
  currentImplementation: {
    id: string;
  };
};

type publicAppSearchQueryVariables = {
  nameContains: string;
  limit: number;
  excludeUpcoming?: boolean;
};

type options = {
  /** limit */
  limit?: number;
  /** searchDebounceDelay */
  searchDebounceDelay?: number;
  /** Excludes upcoming apps from search results */
  excludeUpcoming?: boolean;
  /** The `useQuery` hook to use. */
  useQuery: UseQuery;
};

type useAppSearchDataOutput = OperationVariables & {
  onSearch: (query: string) => void;
  searchQuery: string;
  apps: App[];
};

const DEFAULT_LIMIT = 20;

const timeoutIds = new WeakMap();
const simpleDebounce = (fn: Function, delay: number) => (...args: any) => {
  clearTimeout(timeoutIds.get(fn));
  const timeoutId = setTimeout(() => {
    fn(...args);
  }, delay);
  timeoutIds.set(fn, timeoutId);
};

/**
 * Provides the ability to search for Zapier apps (services, integrations)
 * and receive a set of matching results.
 */
export const useAppSearchData = ({
  limit,
  searchDebounceDelay,
  excludeUpcoming,
  useQuery,
}: options): useAppSearchDataOutput => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const variables: publicAppSearchQueryVariables = {
    limit: limit || DEFAULT_LIMIT,
    nameContains: searchQuery,
    excludeUpcoming,
  };

  const { data, ...graphqlQueryControls } = useQuery(publicAppSearchQuery, {
    variables,
    skip: searchQuery.trim() === '',
  });

  const apps: App[] = data?.getAppsByNameContains?.edges || [];

  return {
    ...graphqlQueryControls,
    onSearch: simpleDebounce(setSearchQuery, searchDebounceDelay || 0),
    searchQuery,
    apps,
  };
};
